import React, {useState, useEffect} from 'react';
import {pdfjs, Document, Page} from 'react-pdf';
import {SizeMe} from 'react-sizeme';
import CircularProgress from '@mui/material/CircularProgress';

import ExerciseAppBar from './ExerciseAppBar';
import SecondaryAppBar from './SecondaryAppBar';
import Card from './Card';
import {APP_READING} from '../constants';

import styles from '../css/PDFViewer.module.css';
import baseStyles from '../css/Base.module.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Used for Reading and Assessment exercises
// Reading exercise is 1 up view for cover, then 2 up for all other pages
// Assessment exercise is always 1 up view
export default function PDFViewer({exercise, mode}) {
  const [pageCount, setPageCount] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageScale, setPageScale] = useState(1);
  const [preferredPageView, setPreferredPageView] = useState(exercise.type === APP_READING ? 2 : 1); // 1 up, 2 up
  const [isPage2Visible, setIsPage2Visible] = useState(false);
  const [isCardVisible, setIsCardVisible] = useState(false);

  useEffect(() => {
    if (exercise._id) {
      setPageNumber(1);
      setPreferredPageView(exercise.type === APP_READING ? 2 : 1);
      setIsPage2Visible(false);
    }
  }, [exercise._id, exercise.type]);

  function onDocumentLoadSuccess({numPages}) {
    setPageCount(numPages);
    setPageNumber(1);
  }

  function onPageLoad(page, containerSize) {
    let scale = containerSize.height / page.originalHeight;

    const pageWidth = page.originalWidth * scale;
    const maxPageWidth = containerSize.width / 2;
    if (pageWidth > maxPageWidth) {
      scale = maxPageWidth / page.originalWidth;
    }

    setPageScale(scale);
  }

  function handleClickCard() {
    setIsCardVisible(!isCardVisible);
  }

  function handlePrevious() {
    let pageOffset = pageNumber === 2 ? 1 : preferredPageView;
    if (pageNumber - pageOffset > 0) setPageNumber(pageNumber - pageOffset);
    else if (pageNumber - 1 > 0) setPageNumber(pageNumber - 1);

    setIsPage2Visible(preferredPageView === 2 && pageNumber - pageOffset - 1 > 0);
  }
  function handleNext() {
    let pageOffset = pageNumber === 1 ? 1 : preferredPageView;
    if (pageNumber + pageOffset <= pageCount) setPageNumber(pageNumber + pageOffset);
    else if (pageNumber + 1 <= pageCount) setPageNumber(pageNumber + 1);

    setIsPage2Visible(preferredPageView === 2 && pageNumber + pageOffset + 1 < pageCount);
  }
  function handleReset() {
    setPageNumber(1);
    setIsCardVisible(false);
  }

  function renderLoading() {
    return (
      <div className={baseStyles['progress-container']}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {mode === 'EXERCISE' && <ExerciseAppBar exercise={exercise} />}
      <SecondaryAppBar
        onClickPrevious={handlePrevious}
        onClickNext={handleNext}
        onClickReset={handleReset}
        onClickCard={handleClickCard}
      />

      <SizeMe monitorHeight>
        {({size}) => (
          <Document
            file={exercise.config.url}
            onLoadSuccess={onDocumentLoadSuccess}
            className={styles.document}
            loading={renderLoading}
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer={false}
              className={styles.page}
              onLoadSuccess={(page) => onPageLoad(page, size)} // assumes pages are same size TODO: add to 2nd page
              scale={pageScale}
            />

            {isPage2Visible && (
              <Page pageNumber={pageNumber + 1} renderTextLayer={false} className={styles.page} scale={pageScale} />
            )}
          </Document>
        )}
      </SizeMe>

      {isCardVisible && <Card onClose={() => setIsCardVisible(false)} />}
    </>
  );
}
